import { messagePreview } from "./chatFiles";

/**
 * Group chats in Messages: a named conversation (kind "group") of several people, run by
 * its moderator (who started it) and the admins they make. What happens in one — created,
 * added, removed, left, renamed, made admin or not, a new moderator — shows in the chat as
 * a line (`message.event`).
 * @see mintly-back/src/controllers/groupChatController.js
 */

const idOf = (value) => String(value?._id ?? value ?? "");

export const isGroupChat = (conversation) => conversation?.kind === "group";

/** "moderator", "admin" or null: what `userId` is in a group. */
export function groupRole(conversation, userId) {
  const id = idOf(userId);
  if (!id) return null;
  if (idOf(conversation?.moderator) === id) return "moderator";
  return (conversation?.admins || []).some((a) => idOf(a) === id) ? "admin" : null;
}

/** The moderator and admins rename a group, add people and remove them. */
export const runsGroup = (conversation, userId) => !!groupRole(conversation, userId);

/**
 * What `userId` may do to `member` in a group: "admin" (make them one), "unadmin" (make
 * them an ordinary member again), "remove". The moderator does all three to anyone; an
 * admin removes people who aren't admins.
 */
export function memberActions(conversation, userId, member) {
  const mine = groupRole(conversation, userId);
  const theirs = groupRole(conversation, member);
  if (!mine || idOf(member) === idOf(userId) || theirs === "moderator") return [];
  if (mine === "moderator") return [theirs === "admin" ? "unadmin" : "admin", "remove"];
  return theirs ? [] : ["remove"];
}

/**
 * Who to suggest for an "@name" being typed in a group: its members whose username starts
 * with `query`, never you, at most 6. Only members can be mentioned in a group.
 */
export function mentionableMembers(conversation, myId, query) {
  const q = String(query || "").toLowerCase();
  return (conversation?.participants || [])
    .filter((p) => p?.username && idOf(p) !== idOf(myId) && p.username.toLowerCase().startsWith(q))
    .sort((a, b) => a.username.localeCompare(b.username))
    .slice(0, 6);
}

export const personName = (user, t) => user?.username || user?.businessName || t("common.user");

/** "Ana, Gio and 3 more": who else is in it, for under the group's name. */
export function memberSummary(conversation, myId, t) {
  const others = (conversation?.participants || []).filter((p) => idOf(p) !== idOf(myId));
  if (!others.length) return t("groups.onlyYou");
  const names = others.slice(0, 3).map((p) => personName(p, t));
  const rest = others.length - names.length;
  return rest > 0 ? t("groups.namesAndMore", { names: names.join(", "), count: rest }) : names.join(", ");
}

/** A group's chat line, in the reader's words: "Nika added Ana", "You left"… */
export function eventText(message, myId, t) {
  const event = message?.event;
  if (!event?.type) return "";
  const mine = idOf(message.sender) === idOf(myId);
  const key = {
    created: "eventCreated",
    added: "eventAdded",
    removed: "eventRemoved",
    left: "eventLeft",
    renamed: "eventRenamed",
    admin: "eventAdmin",
    unadmin: "eventUnadmin",
    moderator: "eventModerator",
  }[event.type];
  if (!key) return "";
  const users = (event.users || [])
    .map((u) => (idOf(u) === idOf(myId) ? t("groups.you") : personName(u, t)))
    .join(", ");
  return t(`groups.${key}${mine ? "ByYou" : ""}`, {
    actor: personName(message.sender, t),
    users,
    name: event.name || "",
  });
}

/** A chat list's line under a group: its latest line, or who said what. */
export function groupPreview(message, myId, t) {
  if (!message) return "";
  if (message.event?.type) return eventText(message, myId, t);
  const text = messagePreview(message, t);
  if (idOf(message.sender) === idOf(myId)) return `${t("groups.youCapital")}: ${text}`;
  // A message that arrived live may name its sender by id only.
  return message.sender && typeof message.sender === "object" ? `${personName(message.sender, t)}: ${text}` : text;
}

/** The line under a chat in a list: a group's latest line or who said what, else the message. */
export function conversationPreview(conversation, myId, t) {
  return isGroupChat(conversation)
    ? groupPreview(conversation.lastMessage, myId, t)
    : messagePreview(conversation?.lastMessage, t);
}

/** Merge a group as the server now has it into a list, keeping each list's own unread count. */
export function upsertGroup(list, conversation) {
  const id = idOf(conversation);
  const existing = list.find((c) => idOf(c) === id);
  if (!existing) return [{ ...conversation, unreadCount: conversation.unreadCount ?? 0 }, ...list];
  return list.map((c) => (idOf(c) === id ? { ...c, ...conversation, unreadCount: c.unreadCount } : c));
}
